import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check, Headset, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLang } from "@/lib/i18n";
import { useSeo, useHreflang } from "@/components/seo/Seo";
import { LANGS, STATIC_COPY, S, p } from "@/lib/seoContent";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const inputCls =
  "w-full rounded-2xl border border-ink/10 bg-white px-4 py-3.5 text-sm font-medium text-ink outline-none transition-colors placeholder:font-normal placeholder:text-slate/60 focus:border-miles focus:ring-4 focus:ring-miles/10";

export default function Contact() {
  const { t, lang } = useLang();
  const seoPage = STATIC_COPY.contact[lang] || STATIC_COPY.contact.fr;
  useSeo({ lang, title: seoPage.title, description: seoPage.meta, path: p(lang, S.contact[lang]), type: "contact", id: "" });
  useHreflang(Object.fromEntries(LANGS.map((l) => [l, p(l, S.contact[l])])));
  const topics = t("co.topics");
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [topicIdx, setTopicIdx] = useState(0);
  const topic = topics[topicIdx] || topics[0];
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, message: `[${topic}] ${form.message}` });
      setSent(true);
      toast.success(t("co.toast_t"), { description: t("co.toast_d") });
    } catch {
      toast.error(t("co.err_t"), { description: t("co.err_d") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-ink" data-testid="contact-page">
      <Navbar />

      <section className="bg-gradient-to-b from-miles-ice/60 to-white pb-16 pt-32 lg:pt-40">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{t("co.eyebrow")}</p>
            <h1 className="mt-5 text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">
              {t("co.h1a")} <span className="font-light text-slate/50">{t("co.h1b")}</span>
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-slate sm:text-lg">
              {t("co.sub")}
            </p>
            <div className="mt-10 space-y-0">
              {t("co.rows").map(([k, v]) => (
                <div key={k} className="group flex items-baseline justify-between gap-6 border-t border-ink/10 py-6 last:border-b">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate">{k}</span>
                  <span className="text-right text-base font-bold tracking-tight text-ink transition-transform duration-300 group-hover:-translate-x-1 sm:text-lg">{v}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="rounded-3xl border border-ink/10 bg-white p-7 shadow-[0_24px_64px_rgba(16,24,40,0.08)] sm:p-9" data-testid="contact-form-wrapper">
            {sent ? (
              <div className="flex min-h-[420px] flex-col items-center justify-center text-center" data-testid="contact-success-message">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-miles text-white shadow-[0_16px_40px_rgba(36,87,255,0.35)]">
                  <Check className="h-7 w-7" strokeWidth={2.5} />
                </span>
                <p className="mt-6 text-2xl font-bold tracking-tight">{t("co.ok_t")}</p>
                <p className="mt-2 max-w-xs text-sm leading-relaxed text-slate">
                  {t("co.ok_b").replace("{name}", form.name.split(" ")[0])}
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div>
                  <p className="mb-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-slate">{t("co.topic_l")}</p>
                  <div className="flex flex-wrap gap-2" data-testid="contact-topic-selector">
                    {topics.map((label, index) => (
                      <button key={label} type="button" onClick={() => setTopicIdx(index)} data-testid={`contact-topic-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all ${
                          topicIdx === index ? "border-miles bg-miles text-white" : "border-ink/10 text-slate hover:border-miles/40 hover:text-ink"
                        }`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <input required value={form.name} onChange={set("name")} placeholder={t("co.name")} data-testid="contact-name-input" className={inputCls} />
                  <input required type="email" value={form.email} onChange={set("email")} placeholder={t("co.email")} data-testid="contact-email-input" className={inputCls} />
                </div>
                <input value={form.company} onChange={set("company")} placeholder={t("co.company")} data-testid="contact-company-input" className={inputCls} />
                <textarea required value={form.message} onChange={set("message")} rows={4} data-testid="contact-message-input"
                  placeholder={t("co.message")} className={`${inputCls} resize-none`} />
                <button type="submit" disabled={loading} data-testid="contact-submit-button"
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-miles py-4 text-sm font-semibold text-white shadow-[0_10px_28px_rgba(36,87,255,0.3)] transition-all hover:-translate-y-0.5 hover:bg-miles-dark disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <ArrowRight className="h-4 w-4" strokeWidth={1.5} />}
                  {loading ? t("co.sending") : t("co.submit")}
                </button>
                <p className="flex items-center justify-center gap-1.5 text-[11px] text-slate">
                  <Headset className="h-3.5 w-3.5 text-miles" strokeWidth={1.5} /> {t("co.note")}
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
